import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Icon } from '../assets/icons';
import { cn } from '../lib/cn';
import { formatCurrencyCompact } from '../lib/currency';
import { TooltipElement } from './tooltip';
import { Typography } from './typography';

export interface IndicatorProps extends ComponentPropsWithoutRef<'div'> {
  value: number;
  negativeValue?: boolean;
  noTooltip?: boolean;
  position?: 'left' | 'right';
  brackets?: boolean;
  negativeAsPositive?: boolean; // Used to reverse behavior, show negative value as positive and vice versa
  showAsDollar?: boolean; // Controls whether the value is displayed in $ or %
  dontShowSuffix?: boolean;
  label?: string | number;
  tooltipMessage?: string;
  tooltipMessagePrefix?: string;
  tooltipStyles?: string;
}

export const Indicator = forwardRef<HTMLDivElement, IndicatorProps>(
  (
    {
      noTooltip = false,
      negativeValue = false,
      position = 'left',
      brackets = false,
      negativeAsPositive = false,
      showAsDollar = false,
      dontShowSuffix = false,
      value,
      className,
      label,
      tooltipMessage = 'Compared to previous period',
      tooltipMessagePrefix,
      tooltipStyles,
      ...props
    },
    ref
  ) => {
    const suffix = dontShowSuffix ? '' : showAsDollar ? '' : '%';

    const percentageValue = negativeValue
      ? `${value}${suffix}`
      : `+${value}${suffix}`;

    const computedValue = showAsDollar
      ? formatCurrencyCompact(value)
      : percentageValue;

    const ArrowComputed = (
      <>
        {negativeValue ? (
          <Icon name="arrow-down" size={16} />
        ) : (
          <Icon name="arrow-up" size={16} />
        )}
      </>
    );

    const nonZeroValueColor =
      negativeValue !== negativeAsPositive
        ? 'text-dust-red-5'
        : 'text-polar-green-7';
    const textColor = !value ? 'text-neutral-7' : nonZeroValueColor;

    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-0.5', textColor, className)}
        {...props}
      >
        {label && <Typography className="text-gray-900">{label}</Typography>}
        {position === 'left' && ArrowComputed}

        <Typography as="p" className="font-medium">
          {brackets ? '(' : ''}
          {computedValue}
          {brackets ? ')' : ''}
        </Typography>

        {position === 'right' && ArrowComputed}
        {!noTooltip && (
          <span className="text-neutral-12 ml-1">
            <TooltipElement
              size={12}
              content={tooltipMessage}
              className={tooltipStyles}
              contentPrefix={tooltipMessagePrefix}
            />
          </span>
        )}
      </div>
    );
  }
);

Indicator.displayName = 'Indicator';
